import { useEffect } from 'react'
import { cn } from '../../lib/shadcn/utils'
import { useGetManagerBookBalance } from '../../hooks/backend/fleet'
import type { ManagerBalanceRow } from '../../utils/managers'

// The balance scoreboard, rendered on Vehicle Allocation and on Fleet.
//
// Balance is judged on operational cars. Cars off the road stay on their
// manager's book and are shown in their own column, so a car going in for
// repairs never makes a book look unbalanced and never invites a reshuffle.
//
// The nudge only appears at a difference of two or more. On a fleet of fourteen
// running cars, a difference of one is noise, and a dashboard that cries wolf
// gets ignored on the day it is right.

export function ManagerBalancePanel({ className }: { className?: string }) {
  const balance = useGetManagerBookBalance()
  const trigger = balance.trigger

  useEffect(() => {
    trigger({}, { skipCache: true })
  }, [trigger])

  const rows = (balance.data as ManagerBalanceRow[] | undefined) ?? []
  if (rows.length === 0) return null

  const operational = rows.map((r) => Number(r.operational_cars))
  const spread = Math.max(...operational) - Math.min(...operational)

  return (
    <div className={cn('rounded-md border border-border bg-card', className)}>
      <div className="flex items-baseline justify-between border-b border-border px-3 py-2">
        <span className="text-xs font-medium">Manager books</span>
        <span className="text-[11px] text-muted-foreground">
          Balance is judged on cars that are on the road
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-muted-foreground">
            <tr className="border-b border-border">
              <th className="px-3 py-1.5 text-left font-medium">Manager</th>
              <th className="px-3 py-1.5 text-right font-medium">On the road</th>
              <th className="px-3 py-1.5 text-right font-medium">Off the road</th>
              <th className="px-3 py-1.5 text-right font-medium">Cars</th>
              <th className="px-3 py-1.5 text-right font-medium">Seats</th>
              <th className="px-3 py-1.5 text-right font-medium">Drivers</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.manager_person_id} className="border-b border-border last:border-b-0">
                <td className="px-3 py-1.5 font-medium">{r.manager_name}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.operational_cars}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                  {r.cars_off_road}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.active_cars}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.seats_this_week}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.active_drivers_on_book}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {spread >= 2 && (
        <div className="border-t border-border px-3 py-2 text-[11px] text-amber-600">
          The running books differ by {spread} cars. Move a car rather than a driver: the
          driver follows the decision you make about the car.
        </div>
      )}
    </div>
  )
}
